import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "../firebase";
import { doc, setDoc, onSnapshot, serverTimestamp, deleteField } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { ArrowRight2, Call, GalleryAdd, InfoCircle, TickCircle, Trash } from "iconsax-reactjs";
import { LOGO_TYPES, prepareLogo } from "../utils/brand";
import { useWideLogo } from "../hooks/useWideLogo";
import "./DealerProfile.css";

// Only logo files this panel uploaded are ever deleted, never the app's avatar.
const isBrandLogoFile = (url) => typeof url === "string" && url.includes("brand-logo-");

export default function DealerProfile({ user, setActiveTab }) {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [contactNumber, setContactNumber] = useState(user?.contactNumber || user?.phoneNumber || "");
  const [offices, setOffices] = useState(Array.isArray(user?.offices) ? user.offices : []);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Portal branding: the logo that replaces GharSetu's in this portal.
  const [logoUrl, setLogoUrl] = useState(user?.brandLogoUrl || null);
  const [logoBusy, setLogoBusy] = useState(null); // "upload" | "remove"
  const [logoMessage, setLogoMessage] = useState("");
  const [logoError, setLogoError] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const logoWide = useWideLogo(logoUrl);

  useEffect(() => {
    if (!user?.uid) return;
    // Fill the form fields from the first snapshot only: the mobile app's
    // presence heartbeat rewrites this doc (lastSeen) every minute, and
    // re-applying name/email/phone on each snapshot wiped whatever the
    // dealer was typing. Offices stay live.
    let formHydrated = false;
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        if (!formHydrated) {
          formHydrated = true;
          if (d.name) setName(d.name);
          if (d.email) setEmail(d.email);
          if (d.contactNumber || d.phoneNumber) setContactNumber(d.contactNumber || d.phoneNumber);
        }
        if (Array.isArray(d.offices)) setOffices(d.offices);
        setLogoUrl(d.brandLogoUrl || null);
      }
    });
    return () => unsub();
  }, [user?.uid]);

  const uploadLogo = async (file) => {
    if (!file || logoBusy) return;
    setLogoMessage("");
    setLogoError("");
    setLogoBusy("upload");
    try {
      const prepared = await prepareLogo(file);
      if (prepared.error) {
        setLogoError(prepared.error);
        return;
      }
      // A new name per upload, so no browser or CDN keeps serving the old logo.
      const logoRef = ref(storage, `avatars/${user.uid}/brand-logo-${Date.now()}.png`);
      await uploadBytesResumable(logoRef, prepared.blob, { contentType: "image/png" });
      const url = await getDownloadURL(logoRef);
      const previous = logoUrl;
      await setDoc(doc(db, "users", user.uid), { brandLogoUrl: url, updatedAt: serverTimestamp() }, { merge: true });
      setLogoUrl(url);
      if (isBrandLogoFile(previous)) {
        deleteObject(ref(storage, previous)).catch((err) => console.warn("Old logo not deleted:", err));
      }
      setLogoMessage("Logo saved. Your portal now shows your brand.");
    } catch (err) {
      console.error("Logo upload failed:", err);
      setLogoError("Couldn't upload your logo. Check your connection and try again.");
    } finally {
      setLogoBusy(null);
      // Lets the same file be picked again after an error.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!logoUrl || logoBusy) return;
    const previous = logoUrl;
    setLogoMessage("");
    setLogoError("");
    setLogoBusy("remove");
    try {
      await setDoc(doc(db, "users", user.uid), { brandLogoUrl: deleteField(), updatedAt: serverTimestamp() }, { merge: true });
      setLogoUrl(null);
      if (isBrandLogoFile(previous)) {
        deleteObject(ref(storage, previous)).catch((err) => console.warn("Old logo not deleted:", err));
      }
      setLogoMessage("Logo removed. Your portal shows the GharSetu logo again.");
    } catch (err) {
      console.error("Logo removal failed:", err);
      setLogoError("Couldn't remove your logo. Please try again.");
    } finally {
      setLogoBusy(null);
    }
  };

  // The whole branding card takes a dropped image file.
  const handleDragOver = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadLogo(e.dataTransfer?.files?.[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    // `required` lets whitespace through, which .trim() then saved as blank.
    if (!name.trim() || !contactNumber.trim()) {
      setError("Business name and contact number can't be empty.");
      return;
    }
    setLoading(true);

    try {
      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        {
          name: name.trim(),
          email: email.trim(),
          contactNumber: contactNumber.trim(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setMessage("Profile settings updated successfully.");
    } catch (err) {
      console.error("Profile update failed:", err);
      setError("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dp-panel">
      <section
        className={`card dp-card dp-brand${dragging ? " is-dragging" : ""}`}
        aria-labelledby="dp-brand-title"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="card-header">
          <div className="dp-card-heading">
            <h2 id="dp-brand-title" className="card-title">
              Portal branding
            </h2>
            <p className="dp-card-desc">
              Your logo replaces the GharSetu logo across your portal: the sidebar, the loading
              screen and the browser tab. Your profile photo in the app stays as it is.
            </p>
          </div>
        </div>

        <div className="card-body dp-brand-body">
          <div className={`dp-logo-tile${logoUrl ? " has-logo" : ""}${logoWide ? " is-wide" : ""}`}>
            {logoUrl ? (
              <img src={logoUrl} alt="Your logo" />
            ) : (
              <GalleryAdd size={28} variant="Linear" aria-hidden="true" />
            )}
          </div>

          <div className="dp-brand-main">
            <span className="dp-brand-status">
              {logoUrl ? "Your logo is showing in this portal" : "Showing the GharSetu logo"}
            </span>
            <span className="field-hint">
              PNG, JPG or WEBP. A square logo of 256 px or more stays sharpest, and a transparent
              background sits cleanly on the white tile. You can also drop the file on this card.
            </span>

            <div className="dp-brand-actions">
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={!!logoBusy}
              >
                {logoBusy === "upload" ? (
                  <>
                    <span className="btn-spinner" aria-hidden="true" />
                    Uploading…
                  </>
                ) : logoUrl ? "Replace logo" : "Upload logo"}
              </button>
              {logoUrl && (
                <button
                  type="button"
                  className="btn-ghost btn-sm dp-logo-remove"
                  onClick={removeLogo}
                  disabled={!!logoBusy}
                >
                  {logoBusy === "remove" ? (
                    <span className="btn-spinner" aria-hidden="true" />
                  ) : (
                    <Trash size={16} variant="Linear" aria-hidden="true" />
                  )}
                  {logoBusy === "remove" ? "Removing…" : "Remove"}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={LOGO_TYPES.join(",")}
                hidden
                onChange={(e) => uploadLogo(e.target.files?.[0])}
              />
            </div>

            <div className="dp-brand-feedback" aria-live="polite">
              {logoMessage && (
                <span className="dp-feedback dp-feedback-success">
                  <TickCircle size={16} variant="Linear" />
                  {logoMessage}
                </span>
              )}
              {logoError && (
                <span className="dp-feedback dp-feedback-error" role="alert">
                  <InfoCircle size={16} variant="Linear" />
                  {logoError}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      <form
        onSubmit={handleSubmit}
        className="card dp-card"
        aria-labelledby="dp-profile-title"
      >
        <div className="card-header">
          <div className="dp-card-heading">
            <h2 id="dp-profile-title" className="card-title">
              Business profile
            </h2>
            <p className="dp-card-desc">
              Your brand identity and public dealer information, shown on the web and in the
              mobile app.
            </p>
          </div>
        </div>

        <div className="card-body dp-grid">
          <div className="field dp-span-2">
            <label className="field-label" htmlFor="dp-name">
              Brand or business name <span className="dp-required">*</span>
            </label>
            <input
              id="dp-name"
              className="input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Apex Realty Group"
              required
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="dp-phone">
              Primary contact number <span className="dp-required">*</span>
            </label>
            <input
              id="dp-phone"
              className="input"
              type="text"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              placeholder="e.g. 9876543210"
              required
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="dp-email">
              Business email
            </label>
            <input
              id="dp-email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. contact@apexrealty.com"
            />
          </div>
        </div>

        <div className="dp-card-footer">
          <div className="dp-feedback-slot" aria-live="polite">
            {message && (
              <span className="dp-feedback dp-feedback-success">
                <TickCircle size={16} variant="Linear" />
                {message}
              </span>
            )}
            {error && (
              <span className="dp-feedback dp-feedback-error" role="alert">
                <InfoCircle size={16} variant="Linear" />
                {error}
              </span>
            )}
          </div>
          <button type="submit" className="btn-primary dp-save" disabled={loading}>
            {loading && <span className="btn-spinner" aria-hidden="true" />}
            {loading ? "Saving changes..." : "Save changes"}
          </button>
        </div>
      </form>

      {/* Registered offices overview */}
      <section className="card dp-card" aria-labelledby="dp-offices-title">
        <div className="card-header">
          <div className="dp-card-heading">
            <div className="dp-title-row">
              <h2 id="dp-offices-title" className="card-title">
                Branch offices
              </h2>
              <span className="count-badge">{offices.length}</span>
            </div>
            <p className="dp-card-desc">Offices registered with your dealer account.</p>
          </div>
          {setActiveTab && (
            <button
              type="button"
              className="btn-secondary dp-manage"
              onClick={() => setActiveTab("dealer-offices")}
            >
              <span>Manage offices</span>
              <ArrowRight2 size={16} variant="Linear" />
            </button>
          )}
        </div>

        {offices.length === 0 ? (
          <p className="dp-offices-empty">
            No offices yet. Add your branch offices to route property enquiries to specific team
            members.
          </p>
        ) : (
          <ul className="dp-office-list">
            {offices.map((loc, idx) => {
              const officeAddress = [loc.address, loc.locality, loc.city].filter(Boolean).join(", ");
              return (
                <li key={loc.id || idx} className="dp-office">
                  <div className="dp-office-main">
                    <div className="dp-office-name-row">
                      <span className="dp-office-name">
                        {loc.officeName || `${loc.locality}, ${loc.city}`}
                      </span>
                      <span className={loc.isPrimary ? "chip chip-primary" : "chip"}>
                        {loc.isPrimary ? "Primary office" : "Branch office"}
                      </span>
                    </div>
                    {officeAddress && <span className="dp-office-address">{officeAddress}</span>}
                  </div>
                  {loc.contactNumber && (
                    <span className="dp-office-phone">
                      <Call size={14} variant="Linear" />
                      {loc.contactNumber}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
